import Event from "./Event";
import Cache from "./Cache";

class Wishlist extends Event {
    constructor() {
        super();
        this.wishlist = {};
        this.cacheDuration = 3600; // seconds
        this.promises = {
            fetching: {},
        };
        this.setUpdatedd = Date.now();
        this.cache = new Cache();

        this.apiURL = "";
        /* eslint-disable no-undef */
        if (process.env.NODE_ENV === "development") {
            this.apiURL = process.env.MIX_API_DEVELOPMENT ? process.env.MIX_API_DEVELOPMENT : "";
        } else {
            this.apiURL = process.env.MIX_API_PRODUCTION ? process.env.MIX_API_PRODUCTION : "";
        }
        /* eslint-enable no-undef */
    }

    // Creating a global instance
    static createInstance() {
        return new this();
    }

    /**
     * Update timestamp
     */
    setUpdated() {
        this.setUpdatedd = Date.now();
    }

    /**
     * Fetch all items from wishlist
     *
     * Wishlist returns pending Promise if API request is pending or
     * returns cached data based on this.cacheDuration value
     *
     * @returns {Promise} Promise object with status and data
     */
    fetchWishlist(preorder = false) {
        this.publish("itemsFetching");

        // Wishlist is cached
        let cachedWishlist = this.cache.get(preorder ? "preorder" : "wishlist");
        if (cachedWishlist)
            return Promise.resolve(this.cache.get(preorder ? "preorder" : "wishlist").data);

        // If fetching is already underway we return promise
        if (this.promises.fetching[preorder ? "preorder" : "wishlist"])
            return this.promises.fetching[preorder ? "preorder" : "wishlist"];

        this.promises.fetching[preorder ? "preorder" : "wishlist"] = fetch(
            this.apiURL + `/${preorder ? "preorder" : "wishlist"}`
        )
            .then((response) => {
                return response.text().then((text) => {
                    return {
                        status: response.status,
                        content: text,
                    };
                });
            })
            .then((response) => {
                let content = response.status < 300 ? JSON.parse(response.content) : null;

                if (content) {
                    this.setUpdated();
                    this.wishlist = content;

                    this.cache.set(preorder ? "preorder" : "wishlist", this.cacheDuration, {
                        status: response.status,
                        content: content,
                    });
                }
                this.publish("itemsFetched", {
                    status: response.status,
                    content: content,
                });
                this.promises.fetching[preorder ? "preorder" : "wishlist"] = null;

                return {
                    status: response.status,
                    content: content,
                };
            })
            .catch((error) => {
                this.setUpdated();
                this.promises.fetching[preorder ? "preorder" : "wishlist"] = null;
                return {
                    status: 600,
                    error: error,
                };
            });

        return this.promises.fetching[preorder ? "preorder" : "wishlist"];
    }

    fetchPreorderForItem(itemId) {
        if(itemId === undefined)
            return Promise.reject("Item ID is undefined");

        this.publish("itemsFetching");

        // Wishlist is cached
        let cachedWishlist = this.cache.get("preorder_"+itemId);
        if (cachedWishlist)
            return Promise.resolve(this.cache.get("preorder_"+itemId).data);

        // If fetching is already underway we return promise
        if (this.promises.fetching["preorder_"+itemId])
            return this.promises.fetching["preorder_"+itemId];

        this.promises.fetching["preorder_"+itemId] = fetch(
            this.apiURL + `/preorder/${itemId}`
        )
            .then((response) => {
                return response.text().then((text) => {
                    return {
                        status: response.status,
                        content: text,
                    };
                });
            })
            .then((response) => {
                let content = response.status < 300 ? JSON.parse(response.content) : null;

                if (content) {
                    this.setUpdated();
                    this.wishlist = content;

                    this.cache.set("preorder_"+itemId, this.cacheDuration, {
                        status: response.status,
                        content: content,
                    });
                }
                this.publish("itemsFetched", {
                    status: response.status,
                    content: content,
                });
                this.promises.fetching["preorder_"+itemId] = null;

                return {
                    status: response.status,
                    content: content,
                };
            })
            .catch((error) => {
                this.setUpdated();
                this.promises.fetching["preorder_"+itemId] = null;
                return {
                    status: 600,
                    error: error,
                };
            });

        return this.promises.fetching["preorder_"+itemId];
    }

    /**
     * Add items to wishlist by array of ids
     *
     * @param {Array} itemArray - Array with book IDs to add
     * @param type
     * @param preorder
     * @returns {Promise} Promise object with status and current wishlist
     */
    addItems(itemArray, type = "book", preorder = false) {
        this.publish("itemsAdding", {preorder: preorder, items: itemArray});

        return fetch(this.apiURL + `/${preorder ? "preorder" : "wishlist"}`, {
            method: "POST",
            cache: "no-cache",
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
                "X-CSRF-TOKEN": document.head.querySelector("meta[name=csrf-token]").content,
            },
            referrerPolicy: "no-referrer",
            body: JSON.stringify({items: itemArray, type: type}),
        })
            .then((response) => {
                return response.text().then((text) => {
                    return {
                        status: response.status,
                        content: text,
                    };
                });
            })
            .then((response) => {
                let content = response.status < 300 ? JSON.parse(response.content) : null;

                if (content) {
                    this.setUpdated();
                    this.wishlist = content;
                    this.cache.set(preorder ? "preorder" : "wishlist", this.cacheDuration, {
                        status: response.status,
                        content: content,
                    });

                    if (content.data.items.length > 0) {
                        const properties = content.data.items[content.data.items.length - 1];
                        if (typeof gtag !== "undefined") {
                            gtag("event", "add_to_wishlist", {
                                currency: "HUF",
                                value: properties.price,
                                items: [
                                    {
                                        id: properties.isbn,
                                        name: properties.title,
                                        brand: properties.publisher,
                                        quantity: 1,
                                        price: properties.price,
                                    },
                                ],
                            });
                        }
                    }
                }
                this.publish("itemsAdded", {
                    status: response.status,
                    preorder: preorder,
                    content: content,
                    type,
                });
                this.promises.fetching[preorder ? "preorder" : "wishlist"] = null;

                return {
                    status: response.status,
                    content: content,
                };
            })
            .catch((error) => {
                this.setUpdated();
                this.promises.fetching[preorder ? "preorder" : "wishlist"] = null;
                this.publish("itemsAdded");
                return {
                    status: 600,
                    error: error,
                };
            });
    }

    /**
     * Remove items from wishlist by array of ids
     *
     * @param {Array} itemArray - Array with book IDs to remove
     * @param type
     * @param preorder
     * @returns {Promise} Promise object with status and current wishlist
     */
    removeItems(itemArray, type = "book", preorder = false) {
        this.publish("itemsRemoving", {preorder: preorder, items: itemArray});

        return fetch(this.apiURL + `/${preorder ? "preorder" : "wishlist"}`, {
            method: "DELETE",
            cache: "no-cache",
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
                "X-CSRF-TOKEN": document.head.querySelector("meta[name=csrf-token]").content,
            },
            referrerPolicy: "no-referrer",
            body: JSON.stringify({items: itemArray, type: type}),
        })
            .then((response) => {
                return response.text().then((text) => {
                    return {
                        status: response.status,
                        content: text,
                    };
                });
            })
            .then((response) => {
                let content = response.status < 300 ? JSON.parse(response.content) : null;

                if (content) {
                    this.setUpdated();
                    this.wishlist = content;
                    this.cache.set(preorder ? "preorder" : "wishlist", this.cacheDuration, {
                        status: response.status,
                        content: content,
                    });
                }
                this.publish("itemsRemoved", {
                    status: response.status,
                    content: content,
                    preorder: preorder,
                    type
                });
                this.promises.fetching = null;

                return {
                    status: response.status,
                    content: content,
                };
            })
            .catch((error) => {
                this.setUpdated();
                this.promises.fetching = null;
                return {
                    status: 600,
                    error: error,
                };
            });
    }

    /**
     * Fetch the number of books in wishlist
     *
     * Wishlist returns pending Promise if API request is pending or
     * returns cached data based on this.cacheDuration value
     *
     * @returns {Promise} Promise object with status and data
     */
    fetchWishlistCount() {
        this.publish("wishlistCountFetching");

        // wishlistCount is cached
        let cachedWishlist = this.cache.get("wishlistCount");
        if (cachedWishlist) return Promise.resolve(this.cache.get("wishlistCount").data);

        // If fetching is already underway we return promise
        if (this.promises.fetching["wishlistCount"]) return this.promises.fetching["wishlistCount"];

        this.promises.fetching["wishlistCount"] = fetch(this.apiURL + "/wishlist/count")
            .then((response) => {
                return response.text().then((text) => {
                    return {
                        status: response.status,
                        content: text,
                    };
                });
            })
            .then((response) => {
                let content = response.status < 300 ? JSON.parse(response.content) : null;

                if (content) {
                    this.setUpdated();
                    this.wishlist = content;

                    this.cache.set("wishlistCount", this.cacheDuration, {
                        status: response.status,
                        content: content,
                    });
                }
                this.publish("wishlistCountFetched", {
                    status: response.status,
                    content: content,
                });
                this.promises.fetching["wishlistCount"] = null;

                return {
                    status: response.status,
                    content: content,
                };
            })
            .catch((error) => {
                this.setUpdated();
                this.promises.fetching["wishlistCount"] = null;
                return {
                    status: 600,
                    error: error,
                };
            });

        return this.promises.fetching["wishlistCount"];
    }
}

export default Wishlist;
